import { Link } from "react-router-dom";
import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";
import { formatDate } from "../../functions";
import { getDecodedToken } from "../../auth";
import StaffIdCard from "../../components/StaffIdCard";

function StaffDirectory() {
  const user = getDecodedToken();
  const [driverDetails, setDriverDetails] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const userRole = user?.roles;
  const userId = user?.userId;

  useEffect(() => {
    axios
      .post(
        `${server}/get_staff_data_mongodb`,
        {},
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      )
      .then((response) => {
        if (response.data && response.data["body-json"]?.body) {
          setDriverDetails(response.data["body-json"].body);
        } else {
          console.error("Invalid data format from API", response.data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("API Error:", error);
        setLoading(false);
      });
  }, []);

  const handleDownloadIDCard = (student) => {
    StaffIdCard(student);
  };

  // Filter
  const filteredRecords = driverDetails.filter((staff) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(staff).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // Calculate pagination
  // Sort by bus number (numerically, not lexically)
  filteredRecords.sort((a, b) => {
    const numA = a.busNumber?.match(/\d+/)?.[0] ?? 0;
    const numB = b.busNumber?.match(/\d+/)?.[0] ?? 0;
    return Number(numA) - Number(numB);
  });

  // Then slice for pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // ================= Excel Export Functions =================

  const exportStaffExcel = (data, fileName, groupByField = null) => {
    const workbook = XLSX.utils.book_new();

    const getValue = (obj, path) => {
      return (
        path.split(".").reduce((acc, key) => (acc ? acc[key] : ""), obj) || ""
      );
    };

    // NEW: formats "2026-08-27" (or any parseable date) -> "27-08-2026"
    const formatDMY = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr; // leave as-is if unparseable
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    // NEW: which columnMapping keys are dates
    const dateFields = ["basicDetails.dob", "basicDetails.doj"];

    const columnMapping = {
      staffId: "Staff ID",
      "basicDetails.initials": "Initials",
      "basicDetails.fullName": "Full Name",
      "basicDetails.gender": "Gender",
      "basicDetails.mobNo": "Mobile No",
      "basicDetails.emailId": "Email ID",
      "basicDetails.dob": "Date of Birth",
      "basicDetails.doj": "Date of Joining",
      "basicDetails.staffImage": "Image",
      "basicDetails.maritalStatus": "Marital Status",
      "basicDetails.correspondenceAddress": "Address",
      "basicDetails.bloodGroup": "Blood Group",
      "categoryDetails.designation": "Designation",
      "categoryDetails.department": "Department",
      "categoryDetails.subject": "Subject",
      "categoryDetails.classTeacher": "Class Teacher",
      "categoryDetails.section": "Section",
      "basicDetails.basicQualification": "Basic Qualification",
      "basicDetails.masterQualification": "Master Qualification",
      "basicDetails.professionalQualification": "Professional Qualification",
      "transportFacility.busNumber": "Bus No",
      "transportFacility.vehicleNo": "Vehicle No",
      "transportFacility.busStop": "Bus Stop",
    };

    const makeSheetData = (list) => {
      return list.map((staff) => {
        const row = {};
        Object.entries(columnMapping).forEach(([path, header]) => {
          let value = getValue(staff, path);

          // NEW: reformat date fields before anything else
          if (dateFields.includes(path)) {
            value = formatDMY(value);
          }

          row[header] = typeof value === "string" ? value.toUpperCase() : value;
        });
        return row;
      });
    };

    // Function to sanitize sheet names
    const sanitizeSheetName = (name) => {
      return (
        (name || "Unknown")
          .replace(/[:\\\/\?\*\[\]]/g, "_") // replace invalid characters
          .substring(0, 31) // Excel max sheet name length = 31
          .trim() || "Sheet"
      );
    };

    if (!groupByField) {
      const worksheet = XLSX.utils.json_to_sheet(makeSheetData(data));
      XLSX.utils.book_append_sheet(workbook, worksheet, "All_Staffs");
    } else {
      const uniqueValues = [
        ...new Set(data.map((s) => getValue(s, groupByField) || "Unknown")),
      ];

      uniqueValues.forEach((group) => {
        const filtered = data.filter(
          (s) => (getValue(s, groupByField) || "Unknown") === group,
        );
        if (filtered.length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(makeSheetData(filtered));
          const safeSheetName = sanitizeSheetName(group);
          XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
        }
      });
    }

    const today = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `${fileName}_${today}.xlsx`);
    toast.success(`Report Generated`);
  };

  // ==============================================
  // 🟢 Individual Report Handlers
  // ==============================================

  // All Staffs (single sheet)
  const handleAllStaffs = () => {
    exportStaffExcel(driverDetails, "All_Staffs");
  };

  // Department Wise (one sheet per department)
  const handleDepartmentWise = () => {
    exportStaffExcel(
      driverDetails,
      "Department_Wise",
      "categoryDetails.department",
    );
  };

  // Gender Wise (one sheet per gender)
  const handleGenderWise = () => {
    exportStaffExcel(driverDetails, "Gender_Wise", "basicDetails.gender");
  };

  // Subject Wise (one sheet per subject)
  const handleSubjectWise = () => {
    exportStaffExcel(driverDetails, "Subject_Wise", "categoryDetails.subject");
  };

  // Marital Status Wise (one sheet per marital status)
  const handleMaritalStatusWise = () => {
    exportStaffExcel(
      driverDetails,
      "Marital_Status_Wise",
      "basicDetails.maritalStatus",
    );
  };

  // Birthday Wise (single sheet sorted by DOB)
  const handleBirthdayWise = () => {
    const sorted = [...driverDetails].sort(
      (a, b) => new Date(a?.basicDetails?.dob) - new Date(b?.basicDetails?.dob),
    );
    exportStaffExcel(sorted, "Birthday_Wise");
  };

  // Qualification Wise (one sheet per qualification)
  const handleQualificationWise = () => {
    exportStaffExcel(
      driverDetails,
      "Qualification_Wise",
      "basicDetails.basicQualification",
    );
  };

  // B.Ed / Non-B.Ed Wise (two sheets)
  const handleBedWise = () => {
    const bedStaff = driverDetails.filter((s) =>
      (s?.basicDetails?.professionalQualification || "")
        .toLowerCase()
        .includes("b.ed"),
    );
    const nonBedStaff = driverDetails.filter(
      (s) =>
        !(s?.basicDetails?.professionalQualification || "")
          .toLowerCase()
          .includes("b.ed"),
    );

    const workbook = XLSX.utils.book_new();

    const toUpper = (val) =>
      typeof val === "string" ? val.toUpperCase() : val;

    if (bedStaff.length)
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(
          bedStaff.map((s) => ({
            "Staff ID": toUpper(s.staffId),
            "Full Name": toUpper(
              `${s.basicDetails?.initials}. ${s.basicDetails?.fullName}`,
            ),
            Department: toUpper(s.categoryDetails?.department),
            Qualification: toUpper(s.basicDetails?.professionalQualification),
          })),
        ),
        "B.Ed",
      );

    if (nonBedStaff.length)
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(
          nonBedStaff.map((s) => ({
            "Staff ID": toUpper(s.staffId),
            "Full Name": toUpper(
              `${s.basicDetails?.initials}. ${s.basicDetails?.fullName}`,
            ),
            Department: toUpper(s.categoryDetails?.department),
            Qualification: toUpper(s.basicDetails?.professionalQualification),
          })),
        ),
        "Non-B.Ed",
      );

    const today = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `BEd_NonBEd_Wise_${today}.xlsx`);
    toast.success("B.Ed / Non-B.Ed Wise Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((staff, index) => (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Staff ID</span>{" "}
                              &mdash; {staff?.staffId}
                              <br />
                              <span className="text-dark">Full Name</span>{" "}
                              &mdash; {staff?.basicDetails?.initials}.{" "}
                              {staff?.basicDetails?.fullName}
                              <br />
                              <span className="text-dark">
                                Designation
                              </span>{" "}
                              &mdash; {staff?.categoryDetails?.designation}
                              <br />
                              <span className="text-dark">
                                Gender
                              </span> &mdash; {staff?.basicDetails?.gender}
                              <br />
                              {(userRole?.includes("Master Admin") ||
                                userRole?.includes("Admin") ||
                                userRole?.includes("Sub Admin")) && (
                                <>
                                  <span className="text-dark">Department</span>{" "}
                                  &mdash; {staff?.categoryDetails?.department}
                                  <br />
                                  <span className="text-dark">
                                    Class Teachership
                                  </span>{" "}
                                  &mdash; {staff?.categoryDetails?.classTeacher}
                                  , {staff?.categoryDetails?.section}
                                  <br />
                                </>
                              )}
                              <span className="text-dark">Mobile No</span>{" "}
                              &mdash; {staff?.basicDetails?.mobNo}
                              <br />
                              <span className="text-dark">Email ID</span>{" "}
                              &mdash;{" "}
                              <span className="text-lowercase">
                                {staff?.basicDetails?.emailId}
                              </span>
                              <br />
                              {(userRole?.includes("Master Admin") ||
                                userRole?.includes("Admin") ||
                                userRole?.includes("Sub Admin")) && (
                                <>
                                  <span className="text-dark">
                                    Date Of Birth
                                  </span>{" "}
                                  &mdash; {formatDate(staff?.basicDetails?.dob)}
                                  <br />
                                  <span className="text-dark">
                                    Date of Joining
                                  </span>{" "}
                                  &mdash; {formatDate(staff?.basicDetails?.doj)}
                                  <br />
                                </>
                              )}
                              <hr className="w-25" />
                              {(userRole?.includes("Master Admin") ||
                                userRole?.includes("Admin") ||
                                userRole?.includes("Sub Admin")) && (
                                <>
                                  <p className="text-dark mb-2">
                                    Qualification Details
                                  </p>
                                  <span className="text-dark">Basic</span>{" "}
                                  &mdash;{" "}
                                  {staff?.basicDetails?.basicQualification}
                                  <br />
                                  <span className="text-dark">Master</span>{" "}
                                  &mdash;{" "}
                                  {staff?.basicDetails?.masterQualification}
                                  <br />
                                  <span className="text-dark">
                                    Professional
                                  </span>{" "}
                                  &mdash;{" "}
                                  {
                                    staff?.basicDetails
                                      ?.professionalQualification
                                  }
                                  <hr className="w-25" />
                                </>
                              )}
                              <span class="text-dark">Stoppage</span> &mdash;{" "}
                              <span className="text-capitalize">
                                {staff?.transportFacility?.busStop || "NONE"}
                              </span>
                              <br />
                              <span class="text-dark">
                                Vehicle No
                              </span> &mdash;{" "}
                              {staff?.transportFacility?.vehicleNo || "N/A"}
                              <br />
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {staff?.transportFacility?.busNumber || "N/A"}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <div className="d-flex align-items-center bg-light rounded-4 mb-2">
                                  <div className="p-2 bg-dark rounded-4 opacity-75">
                                    {!staff.transportFacility.busStop ? (
                                      <img
                                        src="/assets/images/walker.png"
                                        width="22"
                                      />
                                    ) : (
                                      <img
                                        src="/assets/images/transport.png"
                                        width="22"
                                      />
                                    )}
                                  </div>
                                  <div className="p-3">
                                    <p className="mb-0 lh-1 fs-5 text-center text-dark">
                                      {!staff.transportFacility.busStop
                                        ? "Walker"
                                        : "Bus"}
                                    </p>
                                  </div>
                                </div>

                                {(userRole?.includes("Master Admin") ||
                                  userRole?.includes("Admin") ||
                                  userRole?.includes("Sub Admin") ||
                                  userRole?.includes("Principal")) && (
                                  <Link
                                    to="/view-staff"
                                    state={{
                                      staffId: staff?.staffId,
                                    }}
                                    class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    View Details
                                  </Link>
                                )}

                                {(userRole?.includes("Master Admin") ||
                                  userRole?.includes("Admin") ||
                                  userRole?.includes("Sub Admin")) && (
                                  <Link
                                    to="/update-staff"
                                    state={{
                                      staffId: staff?.staffId,
                                    }}
                                    class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Update Details
                                  </Link>
                                )}

                                <Link
                                  onClick={() => handleDownloadIDCard(staff)}
                                  className="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Download ID Card
                                </Link>

                                <Link
                                  to="/update-staff-transport"
                                  state={{
                                    staffId: staff?.staffId,
                                  }}
                                  class="badge bg-info-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Transport
                                </Link>

                                {(userRole?.includes("Master Admin") ||
                                  userRole?.includes("Admin")) && (
                                  <>
                                    <Link class="badge bg-warning-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                      Payroll Details
                                    </Link>
                                  </>
                                )}

                                {(userRole?.includes("Master Admin") ||
                                  userRole?.includes("Admin") ||
                                  userRole?.includes("Sub Admin") ||
                                  userRole?.includes("Principal")) && (
                                  <>
                                    <Link class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                      Attendance
                                    </Link>
                                    <Link class="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                      Book Issue / Return
                                    </Link>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          to="/add-staff"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Staff
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Human Resource</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Staff Directory
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Staff Directory
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.5 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleAllStaffs}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Staffs
                                </span>
                                <span
                                  onClick={handleDepartmentWise}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Department Wise
                                </span>
                                <span
                                  onClick={handleGenderWise}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Gender Wise
                                </span>
                                <span
                                  onClick={handleMaritalStatusWise}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Marital Status Wise
                                </span>
                                <span
                                  onClick={handleBirthdayWise}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Birthday Wise
                                </span>
                                <span
                                  onClick={handleQualificationWise}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Qualification Wise
                                </span>
                                <span
                                  onClick={handleBedWise}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  B.Ed / Non-B.Ed Wise
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default StaffDirectory;
